import { CheckCircleTwoTone, DownloadOutlined, InfoCircleTwoTone } from '@ant-design5/icons';
import { Divider } from 'antd5';
import React, { type Dispatch, type SetStateAction, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { date, getVendorDataColumn, status, type } from 'components/data/inspections';
import { Button } from 'components/ui';
import {
  type ProcessInspectionMetadataFileMutationResult,
  useGetExistingInspectionsForSummaryValidationQuery,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { STANDARD_COLUMN_SCROLL_Y } from 'utils/constants';
import { INSPECTION_VENDOR_NAMES } from 'utils/release-toggles';

import { useUploadInspectionDataModalContext } from '../Context/UploadInspectionDataModalContext';
import { Steps } from '../UploadInspectionDataModal';
import {
  AdditionalInfoText,
  ButtonContainer,
  ValidationTable,
} from '../UploadInspectionDataModal.style';

export type ValidationProps = ProcessInspectionMetadataFileMutationResult['data'] & {
  fileName: string;
  setStep: Dispatch<SetStateAction<Steps>>;
  rollback: {
    update: (toastMessage?: string) => Promise<void>;
    loading: boolean;
  };
  existingInspectionIds?: string[];
};

export const Validation: React.FC<ValidationProps> = ({
  processInspectionMetadataFile,
  fileName,
  setStep,
  rollback,
  existingInspectionIds,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasReleaseToggle } = useAccountContext();
  const hasVendorNames = hasReleaseToggle(INSPECTION_VENDOR_NAMES);
  const { update: _handleRollback, loading: rollbackLoading } = rollback;
  const { setAlertState, setPendingStep, setErroredUploadSteps } =
    useUploadInspectionDataModalContext();

  const { inspections, errorFileUrl, failureCount } = processInspectionMetadataFile!;
  const inspectionCount = inspections.length;

  const { data: existingInspectionData } = useGetExistingInspectionsForSummaryValidationQuery({
    variables: {
      filterBy: [{ key: 'id', values: existingInspectionIds ?? [] }],
    },
    skip: !existingInspectionIds?.length,
  });

  const setAlertStateWarning = () => {
    setAlertState({
      alertTitle: t('assets.rowsContainedInvalidData', {
        count: failureCount,
        filename: fileName,
      }),
      alertType: 'warning',
      alertDescription: t(
        'inspection.validationWarningDescription',
        'Be aware that your inspections will contain incomplete or malformed data.'
      ),
    });
  };

  const handleContinue = () => {
    const nextStep = Steps.DetailsUpload;
    if (isValid) {
      setStep(nextStep);
    } else {
      setPendingStep(nextStep);
      setAlertStateWarning();
    }
  };

  const handleRollback = async (toastMessage?: string) => {
    await _handleRollback(toastMessage);

    setStep(Steps.SummaryUpload);
    setErroredUploadSteps(uploadErrors => ({ ...uploadErrors, summary: false }));
    setAlertState(null);
  };

  const isValid = failureCount === 0 && inspectionCount > 0;

  const { columns, rows } = useMemo(() => {
    if (existingInspectionData?.inspections?.items?.length) {
      const existingInspections = existingInspectionData.inspections.items;
      return {
        columns: [
          {
            key: 'isNew',
            title: '',
            dataIndex: 'isNew',
            render: (value: boolean) =>
              value ? (
                <span>
                  <CheckCircleTwoTone twoToneColor={theme.green} />{' '}
                  {t('inspection.validationCreatedStatus', 'created')}
                </span>
              ) : (
                <span>
                  <InfoCircleTwoTone twoToneColor={theme.lightBlue} />{' '}
                  {t('inspection.validationExistingStatus', 'existing')}
                </span>
              ),
          },
          date,
          type,
          getVendorDataColumn(hasVendorNames),
          status,
        ],
        rows: [
          ...inspections.map(i => ({ isNew: true, ...i })),
          ...existingInspections.map(i => ({ isNew: false, ...i })),
        ],
      };
    }

    return {
      columns: [date, type, getVendorDataColumn(hasVendorNames), status],
      rows: inspections,
    };
  }, [inspections, existingInspectionData, hasVendorNames, theme, t]);

  //TODO new message to use in case user decides ti go to other pages 'Be aware that your inspections will contain incomplete or malformed data.'
  useEffect(() => {
    if (!isValid) {
      setAlertState({
        alertTitle: t('assets.rowsContainedInvalidData', {
          count: failureCount,
          filename: fileName,
        }),
        alertType: 'error',
        alertDescription: t(
          'inspection.validationErrorDescription',
          'Please use the button below to download the error file. Then complete the missing data and upload again.'
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  return (
    <>
      {!isValid && (
        <>
          <Button
            data-testid="download-error-file-btn"
            icon={<DownloadOutlined />}
            href={errorFileUrl}
          >
            {t('assets.downloadErrorFile', 'Download Error File')}
          </Button>
          <AdditionalInfoText>
            <Trans
              i18nKey="inspection.validationNoteText"
              defaults="Note: Horizon requires the inspection details to be formatted in a way that is compatible with our system. For more information on configured inspection details uploads, please visit our <1>User Guide</1>."
              components={[
                // placeholder index 0 unused
                <span key="0" />,
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a
                  key="1"
                  href="https://guide.skyspecs.com/horizon/-MPobGVTLxgvA9kxtck7/"
                  target="_blank"
                  rel="noopener noreferrer"
                />,
              ]}
            />
          </AdditionalInfoText>
          <Divider />
        </>
      )}
      <p>
        {t('inspection.inspectionsSuccessfullyCreated', {
          count: inspectionCount ?? 0,
        })}{' '}
        {existingInspectionIds?.length
          ? t('inspection.existingInspectionsReadyForDetails', {
              count: existingInspectionIds.length,
            })
          : ''}
      </p>

      <ValidationTable columns={columns} dataSource={rows} scroll={STANDARD_COLUMN_SCROLL_Y} />
      <ButtonContainer>
        {isValid ? (
          <>
            <Button
              type="primary"
              danger={true}
              onClick={() => handleRollback(undefined)}
              loading={rollbackLoading}
            >
              {t('inspection.rollBackChanges', 'Roll Back Changes')}
            </Button>
            <Button type="primary" onClick={handleContinue}>
              {t('inspection.continueToDetails', 'Continue to Details')}
            </Button>
          </>
        ) : inspectionCount > 0 ? (
          <>
            <Button
              type="primary"
              danger={true}
              onClick={() =>
                handleRollback(
                  t(
                    'inspection.inspectionsRolledBackReupload',
                    'Created Inspections rolled back. Please reupload with corrected data for errored Inspections.'
                  )
                )
              }
              loading={rollbackLoading}
            >
              {t('inspection.rollBackAndReupload', 'Roll Back & Reupload')}
            </Button>
            <Button type="primary" onClick={handleContinue}>
              {t('inspection.ignoreErrorsAndContinue', 'Ignore Errors & Continue')}
            </Button>
          </>
        ) : (
          <>
            <Button
              type="primary"
              danger={true}
              onClick={() => handleRollback(undefined)}
              loading={rollbackLoading}
            >
              {t('components.reupload', 'Reupload')}
            </Button>
            {!!existingInspectionIds?.length && (
              <Button type="primary" onClick={handleContinue}>
                {t('inspection.ignoreErrorsAndContinue', 'Ignore Errors & Continue')}
              </Button>
            )}
          </>
        )}
      </ButtonContainer>
    </>
  );
};
